/**
 * Часовой пояс на клиенте: догадка браузера и список имён для выбора.
 *
 * ⚠️ Модуль заведён 20.08.2026 затем, чтобы копий догадки стало не две. Они уже
 * были: `features/schedule/common.ts` и полоса часового пояса в каркасе кабинета
 * содержали одну и ту же функцию слово в слово, а разъехаться им было где, у
 * `Intl` есть и пустая строка, и исключение. Общего родителя у расписания и
 * каркаса кабинета нет, поэтому дом у неё здесь: импорт из `lib/` в `features/`
 * нормален, обратный нет (то же правило, что у `lib/plurals.ts`).
 *
 * ⚠️ **Догадка ничего не записывает и записывать не имеет права.**
 * `resolvedOptions()` ошибается на VPN, на чужом ноутбуке и в командировке, а
 * от подтверждённого значения зависит время каждого занятия. Отсюда разделение
 * ролей во всём продукте: догадка это то, ЧТО ПОКАЗАТЬ до подтверждения, а
 * запись это отдельное нажатие человека. Канонизирует имя при этом сервер: у
 * него и у браузера разные версии ICU, и решать, какая строка ляжет в базу,
 * обязана одна сторона.
 */

import { zonesBehaveSame } from "@edu/shared";

/**
 * Догадка браузера о зоне.
 *
 * Пустая строка и отказ `Intl` дают `UTC`: это значение человек всё равно
 * увидит на экране и сможет поправить, а вот пустой список выбора он не увидит
 * никак.
 */
export function guessTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * ── Названия зон по-русски ───────────────────────────────────────────────
 *
 * ⚠️ **Язык захардкожен русским, а не взят из браузера, и это не заглушка.**
 * У приложения нет слоя локализации вовсе: каждая строка интерфейса это
 * русский литерал в коде. Взяв язык из браузера, мы получили бы список зон
 * по-немецки посреди страницы, где всё остальное по-русски, причём у разных
 * людей по-разному, то есть непроверяемо. Когда продукту понадобится второй
 * язык, он понадобится ему целиком, а не в одном поле выбора. Аудитория при
 * этом российская школа и экзамены (раздел 1 CLAUDE.md).
 *
 * ⚠️ **Локализация чисто ЭКРАННАЯ. На проводе и в базе по-прежнему
 * `Europe/Moscow`.** Подпись строится из идентификатора, а не заменяет его:
 * значением `<option>` остаётся имя IANA, канонизирует его сервер, и версия
 * ICU у браузера на это не влияет никак.
 *
 * ⚠️ **ICU русских названий российских городов не отдаёт, и мерить это надо
 * было в браузере, а не в Node.** `Intl.DisplayNames` часовых поясов не умеет
 * вовсе (у него типы language, region, script, currency, calendar,
 * dateTimeField), остаётся `Intl.DateTimeFormat` с `timeZoneName`, и у него в
 * Chrome 145 такая картина по 418 именам реестра:
 *
 * ```
 *                 кириллицей   уникальных подписей   чего не хватает
 * shortGeneric      228/418           418            наши города дал ЛАТИНИЦЕЙ:
 *                                                    Moscow, Kaliningrad, Kirov
 * longGeneric       402/418           154            43 подписи на 281 зону:
 *                                                    «Центральная Европа» это 33
 *                                                    зоны, «Восточная Европа» 15
 * ```
 *
 * То есть один стиль даёт уникальность без русского, другой русский без
 * уникальности, и годного нет ни одного. Node 24 под ICU 78 при этом отдавал
 * `shortGeneric` кириллицей у всех 418, то есть замер на машине разработчика
 * показал бы зелёное там, где у пользователя латиница. Это ровно тот разъезд
 * версий ICU, о котором предупреждает `zonesDiffer` ниже.
 *
 * Отсюда решение: **26 зон России названы здесь руками, остальные берут
 * `shortGeneric`**. Руками именно та группа, ради которой всё делалось, и цена
 * этого честная, см. `RUSSIA` ниже. Остальным `shortGeneric` даёт либо русское
 * название страны («Австрия», «Казахстан»), либо латинский город, то есть ровно
 * то же, что нёс идентификатор IANA, и хуже прежнего не становится нигде.
 */

/** Язык подписей. Одна константа, чтобы поиск по коду находил все места. */
const ZONE_LOCALE = "ru";

/**
 * Часовые пояса России, русскими названиями и в порядке с запада на восток.
 *
 * ⚠️ **Список в коде, и это осознанная плата.** Взять состав из ICU можно
 * (`new Intl.Locale("und-RU").getTimeZones()` отдаёт ровно эти 26), но названия
 * оттуда всё равно приходят латиницей, то есть словарь пришлось бы держать
 * здесь в любом случае. Держать рядом ещё и второй источник состава значило бы
 * завести два места, которые однажды разойдутся.
 *
 * Как это протухнет и что тогда будет видно: Россия меняет зоны редко, но
 * меняет (Волгоград ушёл на UTC+4 в 2018 и вернулся на UTC+3 в 2020, Саратов и
 * Астрахань получили свои зоны в 2016). Новая зона, которой здесь нет, просто
 * уедет в группу «Другие страны» под своим именем от ICU: список станет менее
 * удобным, но не сломается и ничего не потеряет.
 *
 * ⚠️ Смещения тут НЕ записаны намеренно, хотя порядок задан ими. Смещение
 * приходит от ICU в момент показа (`zoneLabel`), потому что оно меняется
 * законом, а порядок между городами при этом остаётся прежним.
 */
const RUSSIA: ReadonlyMap<string, string> = new Map([
  ["Europe/Kaliningrad", "Калининград"],
  ["Europe/Moscow", "Москва"],
  ["Europe/Kirov", "Киров"],
  ["Europe/Volgograd", "Волгоград"],
  ["Europe/Astrakhan", "Астрахань"],
  ["Europe/Saratov", "Саратов"],
  ["Europe/Ulyanovsk", "Ульяновск"],
  ["Europe/Samara", "Самара"],
  ["Asia/Yekaterinburg", "Екатеринбург"],
  ["Asia/Omsk", "Омск"],
  ["Asia/Novosibirsk", "Новосибирск"],
  ["Asia/Barnaul", "Барнаул"],
  ["Asia/Tomsk", "Томск"],
  ["Asia/Novokuznetsk", "Новокузнецк"],
  ["Asia/Krasnoyarsk", "Красноярск"],
  ["Asia/Irkutsk", "Иркутск"],
  ["Asia/Chita", "Чита"],
  ["Asia/Yakutsk", "Якутск"],
  ["Asia/Khandyga", "Хандыга"],
  ["Asia/Vladivostok", "Владивосток"],
  ["Asia/Ust-Nera", "Усть-Нера"],
  ["Asia/Magadan", "Магадан"],
  ["Asia/Sakhalin", "Южно-Сахалинск"],
  ["Asia/Srednekolymsk", "Среднеколымск"],
  ["Asia/Kamchatka", "Петропавловск-Камчатский"],
  ["Asia/Anadyr", "Анадырь"],
]);

/**
 * Зоны бывшего СССР: русское написание и по чему их искать.
 *
 * **Зачем.** ICU называет по-русски СТРАНУ, и только когда она укладывается в
 * одну зону целиком: `Europe/Minsk` это «Беларусь», `Asia/Tashkent` это
 * «Узбекистан». Из этого правила две дыры, и обе ловятся на живых запросах:
 *
 * - у страны с несколькими зонами русского названия нет ни у одной. Все семь
 *   зон Казахстана подписаны латиницей («Almaty», «Aqtobe»), и запрос «казах»
 *   не находил ничего вообще
 * - город под названием страны не ищется: подпись `Europe/Minsk` это
 *   «Беларусь», и «минск» пролетал мимо
 *
 * Поэтому у записи два поля, и делают они разное. `name` ЗАМЕНЯЕТ подпись, и
 * ставится только там, где у ICU русского названия нет вовсе: спорить с ICU о
 * том, «Беларусь» зона или «Минск», мы не собираемся, а вот пустоту заполняем.
 * `find` в подпись не попадает никогда и нужен только поиску.
 *
 * ⚠️ **Граница списка: страны бывшего СССР, и она не случайная.** Аудитория
 * это российская школа и экзамены (раздел 1 CLAUDE.md), а разъезжается она
 * именно сюда. Дальше список не растёт: у остальных стран русское название ICU
 * даёт, а их города ищутся латинским именем IANA, которое и так в поиске
 * («vienna» находит Австрию). Расширять это до мирового справочника городов
 * значило бы завести вторую CLDR руками.
 */
interface ZoneAlias {
  /** Чем заменить подпись. Только там, где ICU не дал русского названия. */
  readonly name?: string;
  /** Дополнительные слова для поиска. На экран не попадают никогда. */
  readonly find: string;
}

const ALIASES: ReadonlyMap<string, ZoneAlias> = new Map([
  // Казахстан: семь зон, и ни одну ICU по-русски не называет, потому что
  // страна не укладывается в одну зону. Здесь и подпись, и поиск
  ["Asia/Almaty", { name: "Алматы", find: "Алма-Ата Казахстан" }],
  ["Asia/Aqtau", { name: "Актау", find: "Казахстан" }],
  ["Asia/Aqtobe", { name: "Актобе", find: "Актюбинск Казахстан" }],
  ["Asia/Atyrau", { name: "Атырау", find: "Казахстан" }],
  ["Asia/Oral", { name: "Уральск", find: "Казахстан" }],
  ["Asia/Qostanay", { name: "Костанай", find: "Кустанай Казахстан" }],
  ["Asia/Qyzylorda", { name: "Кызылорда", find: "Казахстан" }],
  // Тоже без русского названия у ICU: страна многозонная либо спорная
  ["Asia/Samarkand", { name: "Самарканд", find: "Узбекистан" }],
  ["Europe/Simferopol", { name: "Симферополь", find: "Крым" }],
  // А здесь ICU название СТРАНЫ даёт, и оно остаётся. Дописан только город,
  // иначе «минск» не находит «Беларусь»
  ["Asia/Tashkent", { find: "Ташкент" }],
  ["Asia/Bishkek", { find: "Бишкек Кыргызстан" }],
  ["Asia/Dushanbe", { find: "Душанбе" }],
  ["Asia/Ashgabat", { find: "Ашхабад" }],
  ["Asia/Baku", { find: "Баку" }],
  ["Asia/Yerevan", { find: "Ереван" }],
  ["Asia/Tbilisi", { find: "Тбилиси" }],
  ["Europe/Minsk", { find: "Минск" }],
  ["Europe/Kyiv", { find: "Киев" }],
  ["Europe/Chisinau", { find: "Кишинёв Кишинев" }],
  ["Europe/Riga", { find: "Рига" }],
  ["Europe/Tallinn", { find: "Таллин Таллинн" }],
  ["Europe/Vilnius", { find: "Вильнюс" }],
]);

/** Кусок отформатированной даты, отвечающий за имя зоны. Пустая строка при отказе. */
function zonePart(zone: string, style: "shortGeneric" | "shortOffset"): string {
  try {
    return (
      new Intl.DateTimeFormat(ZONE_LOCALE, { timeZone: zone, timeZoneName: style })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    // Незнакомое браузеру имя зоны: у него ICU старше нашего сервера. Подписи
    // не будет, останется сам идентификатор, и это лучше пустого места
    return "";
  }
}

/**
 * Название зоны по-русски: «Москва», «Екатеринбург», «Австрия», `America/Yakutat`.
 *
 * Откат на сам идентификатор в двух случаях: браузер зоны не знает и подписи
 * нет вовсе, либо подпись это «GMT+5», то есть названия у ICU нет. `UTC`
 * попадает во второй случай и остаётся `UTC`, что и правильно: это не место, а
 * обозначение.
 */
export function zoneName(zone: string): string {
  const own = RUSSIA.get(zone) ?? ALIASES.get(zone)?.name;
  if (own !== undefined) return own;
  const name = zonePart(zone, "shortGeneric");
  return name === "" || name.startsWith("GMT") ? zone : name;
}

/** Две половины подписи зоны. Склеивает их `zoneLabel`, и только он. */
export interface ZoneLabelParts {
  /** Место: «Москва», «Австрия», `America/Yakutat`. */
  readonly name: string;
  /** Текущее смещение: «GMT+3». Пустая строка, если ICU его не дал. */
  readonly offset: string;
}

/**
 * Подпись зоны по половинам.
 *
 * ⚠️ **Заведено 17.09.2026 ради ОДНОГО места: кнопки пояса на телефоне.** Там
 * половины показываются в другом порядке («GMT+12, Петропавловск-Кам…»), потому
 * что обрезаться при нехватке ширины обязано имя города, а смещение обязано
 * остаться целым: смещение это то единственное, по чему человек ловит
 * расхождение со своими часами, а название места он и так знает.
 *
 * ⚠️ **Порядок и знаки препинания там делают СТИЛИ, а не вторая склейка.**
 * Строка подписи собирается ровно в одном месте, `zoneLabel` ниже, и второе её
 * написание разъехалось бы молча: у одной половины продукта зона стала бы
 * «Москва (GMT+3)», у другой «Москва, GMT+3», и заметить это можно было бы
 * только рядом.
 */
export function zoneLabelParts(zone: string): ZoneLabelParts {
  return { name: zoneName(zone), offset: zonePart(zone, "shortOffset") };
}

/**
 * Зона так, как её показывают человеку: «Москва (GMT+3)».
 *
 * ⚠️ Смещение в подписи обязательно, и оно не украшение. Названия вроде
 * «Австрия» и «Казахстан» не говорят, сколько сейчас на часах, а выбирают зону
 * ровно ради этого. Смещение ТЕКУЩЕЕ, то есть летом у Вены это GMT+2, а зимой
 * GMT+1: человек сверяет подпись со своими часами прямо сейчас.
 */
export function zoneLabel(zone: string): string {
  const { name, offset } = zoneLabelParts(zone);
  return offset === "" ? name : `${name} (${offset})`;
}

export interface ZoneOption {
  /** Имя IANA: то, что уедет на сервер. */
  readonly zone: string;
  /** То, что прочитает человек. */
  readonly label: string;
}

export interface ZoneGroup {
  /** Заголовок `<optgroup>`. `null` означает список без группировки. */
  readonly title: string | null;
  readonly zones: readonly ZoneOption[];
}

/** Список всех имён реестра. Пустой, если браузер `supportedValuesOf` не умеет. */
function supportedZones(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    return intl.supportedValuesOf?.("timeZone") ?? [];
  } catch {
    return [];
  }
}

/**
 * Зоны для выбора руками, сгруппированные и подписанные по-русски.
 *
 * **Россия отдельной группой и первой.** 418 зон одним списком это список, в
 * котором свою зону ищут прокруткой, а нашей аудитории из этих 418 нужны 26.
 * Порядок внутри группы задан не алфавитом, а долготой, с запада на восток: так
 * эти зоны и называют. У остальных порядок по алфавиту подписи, потому что
 * искать их будут по названию страны.
 *
 * ⚠️ `ensure` это не удобство, а условие работоспособности выбора. В
 * `Intl.supportedValuesOf` нет ни `UTC`, который браузеры докладывают и сервер
 * принимает, ни старых представителей уже переименованных зон
 * (`Asia/Calcutta`, `Europe/Kiev`). `<select>` со значением, которого нет среди
 * `<option>`, показывает пустоту вместо выбранного: человек с записанным `UTC`
 * увидел бы пустой список и, нажав «Сохранить», записал бы обратно ровно то же
 * значение. То есть окно, заведённое ради смены пояса, молча не делало бы
 * ничего.
 *
 * ⚠️ Считается ЛЕНИВО и запоминается: подписи это два обращения к ICU на зону,
 * то есть под тысячу на список, и на телефоне это заметно. Раз на сессию это
 * плата за открытие окна, на каждую отрисовку было бы подвисание.
 */
const groupsCache = new Map<string, ZoneGroup[]>();

export function timeZoneGroups(...ensure: readonly string[]): ZoneGroup[] {
  const wanted = [...new Set(ensure.filter((zone) => zone !== ""))].sort();
  const key = wanted.join("|");
  const known = groupsCache.get(key);
  if (known !== undefined) return known;

  const all = supportedZones();
  const pool = [...wanted.filter((zone) => !all.includes(zone)), ...all];
  const option = (zone: string): ZoneOption => ({ zone, label: zoneLabel(zone) });
  const order = [...RUSSIA.keys()];

  const groups: ZoneGroup[] = [
    {
      title: "Россия",
      // Порядок берётся из самого словаря, а не из сортировки по смещению:
      // у Кирова, Москвы и Волгограда смещение одно, а порядок между ними есть
      zones: order.filter((zone) => pool.includes(zone)).map(option),
    },
    {
      title: "Другие страны",
      zones: pool
        .filter((zone) => !RUSSIA.has(zone))
        .map(option)
        .sort((a, b) => a.label.localeCompare(b.label, ZONE_LOCALE)),
    },
  ].filter((group) => group.zones.length > 0);

  groupsCache.set(key, groups);
  return groups;
}

/**
 * Оставить в группах только зоны, подходящие под введённый текст.
 *
 * **Зачем.** 418 строк это список, в котором свою зону ищут прокруткой, а
 * группировка сузила поиск только для России. Просьба владельца дословно:
 * «чтобы пользователь не крутил полчаса».
 *
 * ⚠️ **Ищем по трём вещам: подписи, имени IANA и словарю синонимов.** По
 * подписи очевидно («ека» это Екатеринбург). По идентификатору неочевидно, но
 * нужно: 164 зоны из 392 в группе «Другие страны» ICU по-русски не называет
 * вовсе, и «Almaty» это единственное, что там написано; плюс это лечит забытую
 * раскладку, набранное «mosc» найдёт Москву, хотя в подписи у неё ни одной
 * латинской буквы. Синонимы закрывают то, чего не закрывают первые два:
 * русские написания городов бывшего СССР, см. `ALIASES`.
 *
 * ⚠️ **Смещение ищется тем же вхождением, отдельной ветки у него нет.**
 * Подпись это «Москва (GMT+3)», поэтому «+3» находит все зоны с этим
 * смещением, а «gmt+5» находит и «GMT+5», и «GMT+5:30». Так и задумано: человек,
 * набравший смещение, ищет по нему, а не по точному совпадению.
 *
 * Пустые группы выбрасываются целиком: заголовок «Россия» над пустотой это
 * утверждение, что в России ничего не нашлось, и читается оно как поломка.
 *
 * Пустой запрос отдаёт исходные группы тем же объектом, а не копией: список
 * без фильтра это самый частый случай, и перебирать ради него 418 строк на
 * каждое нажатие клавиши незачем.
 */
export function filterZoneGroups(
  groups: readonly ZoneGroup[],
  query: string,
): readonly ZoneGroup[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return groups;
  const hit = (option: ZoneOption): boolean =>
    option.label.toLowerCase().includes(needle) ||
    option.zone.toLowerCase().includes(needle) ||
    (ALIASES.get(option.zone)?.find.toLowerCase().includes(needle) ?? false);

  const out: ZoneGroup[] = [];
  for (const group of groups) {
    const zones = group.zones.filter(hit);
    if (zones.length > 0) out.push({ title: group.title, zones });
  }
  return out;
}

/** Единственная зона среди групп, либо `null`, если их не одна. */
export function onlyZone(groups: readonly ZoneGroup[]): string | null {
  if (groups.length !== 1 || groups[0]?.zones.length !== 1) return null;
  return groups[0]?.zones[0]?.zone ?? null;
}

/**
 * Спорят ли две зоны, то есть показывают ли они разное время.
 *
 * ⚠️ **Сравнение ПО ПОВЕДЕНИЮ, а не по строке, и это правило, а не
 * оптимизация.** Представитель зоны зависит от версии ICU: браузер докладывает
 * `Europe/Kyiv`, а сервер под другой ICU записал `Europe/Kiev`, и сравнение
 * строкой объявило бы расхождением переименование. Человеку это показывается
 * как «часы устройства показывают другое», то есть как факт, которого нет.
 *
 * Живёт здесь, а не в каждом месте по копии, потому что мест этих два и они на
 * разных экранах: строка «время в зоне …» на расписании и окно выбора пояса.
 * До 20.08.2026 копий было именно две, и вторая сравнивала строкой.
 *
 * ⚠️ Это НЕ то же самое, что зона СЕРИИ занятий. Там совпадение поведения не
 * означает совпадения зон (в одной группе с Москвой сидят Стамбул и Волгоград),
 * и решение у неё своё. Здесь зона одного человека: она влияет только на его
 * собственное отображение, и совпавшее поведение значит ровно то же время на
 * экране.
 *
 * Незнакомое имя у одной из сторон означает «не спорят»: сообщение о
 * расхождении всё равно назвало бы то, чего мы не разбираем.
 */
export function zonesDiffer(a: string | null, b: string): boolean {
  if (a === null) return false;
  try {
    return !zonesBehaveSame(a, b);
  } catch {
    return false;
  }
}
